import { forwardRef } from "react";

/**
 * Site logo with modern-format fallback chain:
 *   AVIF (best compression, Chrome 85+ / Safari 16.4+ / Firefox 93+)
 *   → WebP (Chrome / Safari 14+ / Firefox / Edge)
 *   → PNG   (universal, guaranteed)
 *
 * Browsers pick the first `<source>` they support; the `<img>` is the ultimate
 * fallback. Same-origin (served from /public) so no CDN dependency.
 *
 * Props:
 *   variant       : 'light' (default, uses transparent logo) | 'dark' (variant for dark backgrounds)
 *   className     : forwarded to <img> for sizing (e.g., "h-14 w-auto")
 *   alt           : image alt text
 *   priority      : true → fetchPriority=high (Navbar); false → loading=lazy (Footer, etc.)
 *   width, height : intrinsic dimensions used to reserve layout space (prevents CLS)
 */
const LOGO = {
    light: {
        avif: "/logo-anand-tutorials.avif",
        webp: "/logo-anand-tutorials.webp",
        png: "/logo-anand-tutorials.png",
        w: 320, h: 213,
    },
    dark: {
        avif: "/logo-anand-tutorials-dark.avif",
        webp: "/logo-anand-tutorials-dark.webp",
        png: "/logo-anand-tutorials-dark.png",
        w: 320, h: 116,
    },
};

const Logo = forwardRef(function Logo(
    { variant = "light", className = "h-14 w-auto", alt = "Anand Tutorials", priority = false, width, height, ...rest },
    ref
) {
    const src = LOGO[variant] || LOGO.light;
    return (
        <picture>
            <source srcSet={src.avif} type="image/avif" />
            <source srcSet={src.webp} type="image/webp" />
            <img
                ref={ref}
                src={src.png}
                alt={alt}
                width={width || src.w}
                height={height || src.h}
                decoding="async"
                {...(priority
                    ? { fetchPriority: "high" }
                    : { loading: "lazy" })}
                className={className}
                {...rest}
            />
        </picture>
    );
});

export default Logo;
